import React, { FC, Fragment } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Grid, CardContent, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { UserGroup } from 'types/app';
import { Separator } from 'components';
import { useClient, useContent } from 'hooks';
import { IPostRenderProps } from 'features/posting';
import * as paths from 'paths';

import {
  Commentary,
  Date,
  Discussions,
  Engage,
  Engagements,
  Groups,
  Reactions,
  Pin,
  Pinned,
  Share,
  EngagementCount,
} from '../widgets';
import { getApprovedGroups, isEngageable, isShareable } from '../helpers';
import { CommentCountModal, ReactionCountModal, ShareCountModal } from '../widgets/CountModal';

interface IProps {
  selectedGroup?: UserGroup;
  toggleState: IPostRenderProps['toggleState'];
}

const useStyles = makeStyles(theme => ({
  content: {
    paddingBottom: theme.spacing(1),
  },
  count: {
    marginRight: theme.spacing(2),
  },
}));

const Meta: FC<IProps> = ({ toggleState }) => {
  const classes = useStyles();
  const content = useContent();
  const { path } = useRouteMatch();
  const { disable_comments } = useClient();
  const { content_id, publish_at } = content;

  const showShare = isShareable(content);
  const showEngage = isEngageable(content);
  const approvedGroups = getApprovedGroups(content);
  const isPermalinkPage = path === paths.content;
  const showCommenting = !disable_comments && !isPermalinkPage;

  function renderCounts() {
    return (
      <Grid container justify="flex-start" alignItems="center">
        <Grid item className={classes.count}>
          <ReactionCountModal />
        </Grid>
        {showCommenting && (
          <Grid item className={classes.count}>
            <CommentCountModal />
          </Grid>
        )}
        {showShare && (
          <Grid item className={classes.count}>
            <ShareCountModal />
          </Grid>
        )}
        {showEngage && (
          <Grid item>
            <EngagementCount />
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Fragment>
      <CardContent>
        <Grid container alignItems="center">
          <Pin />
          <Groups groups={approvedGroups} />
          {!!approvedGroups.length && <Separator />}
          <Date contentId={content_id} publishAt={publish_at} />
        </Grid>
      </CardContent>
      <CardContent>{renderCounts()}</CardContent>
      <Engagements />
      <CardContent className={classes.content}>
        <Grid container alignItems="center" justify="space-between">
          <Grid container item xs={4} alignItems="center">
            <Grid item>
              <Reactions />
            </Grid>
            {showCommenting && (
              <Grid item>
                <Discussions />
              </Grid>
            )}
          </Grid>
          <Grid container justify="flex-end" item xs={8} alignItems="center" spacing={1}>
            {showShare && (
              <Grid item>
                <Share />
              </Grid>
            )}
            {showEngage && (
              <Grid item>
                <Engage />
              </Grid>
            )}
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <Pinned toggleState={toggleState} />
      <Commentary toggleState={toggleState} />
    </Fragment>
  );
};

export default Meta;
